import { useState, useMemo, FC } from 'react';
import { useSnackbar } from 'notistack';
import { Table, Pagination, Loader } from '../../../../components';
import { Button, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Edit, Delete } from '@mui/icons-material';
import { getPartAbbreviations, deletePartAbbreviations } from '../../../../fetch';
import { IPartAbbreviation, IPartAbbreviationsRes } from '../../../../models';
import { PartAbbreviationModal } from '../modals/part-abbreviation-modal';
import { useQuery } from 'react-query';

interface IPartAbbreviationsFile {
  isModalOpen: boolean;
  setModalOpen: (val: boolean) => void;
}

export const PartAbbreviations: FC<IPartAbbreviationsFile> = ({ isModalOpen, setModalOpen }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [currentPartAbbreviation, setCurrentPartAbbreviation] = useState<IPartAbbreviation>(null);
  const classes = useStyles();

  const [page, setPage] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);

  const {
    isLoading,
    data: partAbbreviations,
    refetch
  } = useQuery<IPartAbbreviationsRes, Error>(
    ['get-part-abbreviations', page, perPage],
    () =>
      getPartAbbreviations({
        page: page + 1,
        perPage
      }),
    {}
  );

  const handleDelete = async id => {
    const result = window.confirm('Are you sure you want to delete this option?');
    if (result) {
      setDeleting(true);
      try {
        await deletePartAbbreviations(id);
        refetch();
        enqueueSnackbar('Option Deleted!', {
          variant: 'success'
        });
      } catch (error) {
        var errorMessage = error?.response?.data?.Detail;
        if (errorMessage) {
          enqueueSnackbar(errorMessage, {
            variant: 'error'
          });
        } else {
          enqueueSnackbar('Error, unable to delete option, please try again', {
            variant: 'error'
          });
        }
      } finally {
        setDeleting(false);
      }
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Option',
        accessor: 'abbreviation',
        sort: false
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPartAbbreviation } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <Button
                disabled={isDeleting}
                id="edit"
                color="primary"
                startIcon={<Edit />}
                onClick={() => {
                  setCurrentPartAbbreviation(original);
                  setModalOpen(true);
                }}
              >
                Edit
              </Button>
              <Button
                disabled={isDeleting}
                className={classes.deleteButton}
                startIcon={<Delete />}
                onClick={() => {
                  handleDelete(original.partAbbreviationId);
                }}
              >
                Delete
              </Button>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partAbbreviations, isDeleting]);

  return (
    <>
      {(isLoading || !partAbbreviations || isDeleting) && (
        <Loader position="centered" type={isDeleting ? 'fullscreen' : 'overlay'}>
          Loading...
        </Loader>
      )}
      {partAbbreviations && (
        <>
          <Table
            columns={columns}
            data={partAbbreviations?.records}
            isLoading={isLoading}
            hidePagination
            mobileProps={{
              handleEdit: (val: IPartAbbreviation) => setCurrentPartAbbreviation(val),
              handleDelete: val => handleDelete(val.territoryId)
            }}
          />
          <Pagination
            page={page}
            count={partAbbreviations?.totalRecordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setPerPage}
          />
        </>
      )}
      {isModalOpen && (
        <PartAbbreviationModal
          onClose={(shouldRefetch: boolean) => {
            if (shouldRefetch) {
              refetch();
            }
            setModalOpen(false);
            setCurrentPartAbbreviation(null);
          }}
          open
          currentPartAbbreviation={currentPartAbbreviation}
        />
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  actionButtons: {
    display: 'flex',
    textAlign: 'right'
  },
  editButton: {
    textAlign: 'right'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}));
